import { FC, useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuidv4 } from 'uuid';
import { MasterDataTable } from "../../../components/DataTable/MasterDataTable";
import { TableColumn } from "react-data-table-component";
import { Modal } from "bootstrap";
import { AddRole } from "../../../components/Roles/AddRole";
import { Role } from "../../../interfaces/Role";
import { UpdateRole } from "../../../components/Roles/UpdateRole";
import { DeleteRole } from "../../../components/Roles/DeleteRole";
import { IconButton, Menu, MenuItem } from "@mui/material";
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useIntl } from "react-intl";
import { Link } from "react-router-dom";
import useDynamicTab from "../../../hooks/useDynamicTab";
import { MasterDataTableTanstack } from "../../../components/DataTable/MasterDataTableTanstack";
import { CellContext, ColumnDef } from "@tanstack/react-table";
import useUser from "../../../hooks/User";
import moment from "moment-timezone";

const MasterRoles: FC = () => {
    const intl = useIntl()
    const user = useUser()
    useDynamicTab({ title: `${intl.formatMessage({ id: 'MENU.ROLES' })} | CRM WooBlazz` });
    
    const [role, setRole] = useState<Role>();
    const [showUpdateModal, setShowUpdateModal] = useState<boolean>(false);
    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
    const [openMenuId, setOpenMenuId] = useState<string | null>(null);

    const anchorRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});

    useEffect(() => {
        if (showUpdateModal && role) {
            const modalElement = document.getElementById(`update-role-modal-${role.id}`);

            if (modalElement) {
                const modalInstance = new Modal(modalElement);
                modalInstance.show();

                // This listener sets showChat to false when the modal is closed
                const handleModalHide = () => {
                    setShowUpdateModal(false);
                };

                // Attach the event listener
                modalElement.addEventListener('hidden.bs.modal', handleModalHide);

                // Clean up the listener when the component is unmounted or if showChat/chatHistory changes
                return () => {
                    modalElement.removeEventListener('hidden.bs.modal', handleModalHide);
                };
            }
        }
    }, [showUpdateModal, role, setShowUpdateModal]);

    useEffect(() => {
        if (showDeleteModal && role) {
            const modalElement = document.getElementById(`delete-role-modal-${role.id}`);

            if (modalElement) {
                const modalInstance = new Modal(modalElement);
                modalInstance.show();

                // This listener sets showChat to false when the modal is closed
                const handleModalHide = () => {
                    setShowDeleteModal(false);
                };

                // Attach the event listener
                modalElement.addEventListener('hidden.bs.modal', handleModalHide);

                // Clean up the listener when the component is unmounted or if showChat/chatHistory changes
                return () => {
                    modalElement.removeEventListener('hidden.bs.modal', handleModalHide);
                };
            }
        }
    }, [showDeleteModal, role, setShowDeleteModal]);

    const handleClick = (menuId: string) => {
        setOpenMenuId(menuId);
    };
    const handleClose = () => {
        setOpenMenuId(null);
    };

    const columns = useMemo<ColumnDef<Role>[]>(() => [
        {
            id: 'actions',
            meta: {
                styleHeader: {
                    width: '80px',
                    // textAlign: 'center'
                },
                styleBody: {
                    width: '80px',
                    // textAlign: 'center'
                }
            },
            header: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.ACTION' }),
            cell: (props: CellContext<Role, unknown>) => {
                const row = props.row.original
                return (
                    <>
                        {row && (
                            <>
                                <IconButton
                                    aria-label={`more-${row.id}`}
                                    id={`menu-anchor-${row.id}`}
                                    onClick={() => handleClick(row.id)}
                                    ref={(el) => { anchorRefs.current[row.id] = el }}
                                >
                                    <MoreVertIcon />
                                </IconButton>
                                <Menu
                                    id={`menu-${row.id}`}
                                    anchorEl={anchorRefs.current[row.id]}
                                    open={openMenuId === row.id}
                                    onClose={handleClose}
                                    disableScrollLock={true}
                                >
                                    <MenuItem onClick={() => {
                                        handleClose();
                                        setShowUpdateModal(true);
                                        setRole(row);
                                    }}>
                                        <i className="fas fa-edit fs-4 me-4"></i>
                                        <span>{intl.formatMessage({ id: 'FORM.ACTION.UPDATE' })}</span>
                                    </MenuItem>

                                    <Link to={`lead-progress-access/${row.name}~${btoa(row.id)}`} className='text-black'>
                                        <MenuItem onClick={() => {
                                            handleClose();
                                        }}>
                                            <i className="fas fa-eye fs-4 me-4"></i>
                                            <span>{intl.formatMessage({ id: 'FORM.ACTION.PROGRESS_SETTINGS' })}</span>
                                        </MenuItem>
                                    </Link>

                                    <MenuItem onClick={() => {
                                        handleClose();
                                        setShowDeleteModal(true);
                                        setRole(row);
                                    }}>
                                        <i className="fas fa-trash fs-3 me-5"></i>
                                        <span>{intl.formatMessage({ id: 'FORM.ACTION.DELETE' })}</span>
                                    </MenuItem>
                                </Menu>
                            </>
                        )}
                    </>
                )
            }
        }, 
        {
            id: uuidv4(),
            meta: {
                styleHeader: {
                    width: '650px',
                },
                styleBody: {
                    width: '650px',
                }
            },
            header: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.NAME' }),
            accessorKey: 'name',
        },
        {
            id: 'dateCreated',
            header: intl.formatMessage({id: 'MASTERS.DATATABLE.TYPE.DATE_CREATED'}),
            cell: (props: CellContext<any, unknown>) => {
                const row = props.row.original
                return <div style={{
                    maxWidth: '250px',
                    display: "-webkit-box",
                    WebkitLineClamp: 4, 
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                }}>
                    {moment(row.date_created).utc().format('DD MMM YYYY HH:mm')}
                </div>
            },
            accessorKey: 'date_created',
            // accessorFn: (row) => row.date_created ? moment(row.date_created).toISOString() : '',
        }
    ], [intl, openMenuId]);

    const actions = [
        <AddRole />
    ]

    return (
        <>
            <ol className="breadcrumb text-muted fs-6 fw-bold mb-5">
                <li className="breadcrumb-item text-dark">{intl.formatMessage({ id: 'MENU.ROLES' })}</li>
            </ol>
            <MasterDataTableTanstack
                tableKey="roles"
                tableColumns={columns}
                actions={actions}
                apiURL="roles"
                autoReload={true}
                orderBy="dateCreated"
                orderType="DESC"
            />
            {
                showUpdateModal && <UpdateRole role={role!} />
            }
            {
                showDeleteModal && <DeleteRole role={role!} />
            }
        </>
    )
}

export { MasterRoles }